import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Box,
  Skeleton,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  fetchUserTradeSignals,
  fetchUserTradeSignalStats,
  setFilters,
  selectUserTradeSignals,
  selectUserTradeSignalSummary,
  selectUserTradeSignalPagination,
  selectUserTradeSignalFilters,
  selectUserTradeSignalStats,
  selectUserTradeSignalsLoading,
  selectUserTradeSignalsError,
  selectUserTradeSignalTableStats
} from './userTradeSignalsSlice';
import { CardSkeleton, TableSkeleton } from '../../components/LoadingSpinner';
import TradeChartModal from '../../components/TradeChartModal';
import DWLFLoader from '../../components/DWLFLoader';
import EmptyState from '../../components/common/EmptyState';
import { TrendingUp as SignalIcon } from '@mui/icons-material';
import { stopInForce, trailingStopLevel, trailingExit } from '../../utils/trailingStop';

const defaultFilters = {
  symbol: '',
  strategy: '',
  active: '',
  fromDate: '',
  toDate: '',
  hit3R: false,
  stoppedOut: false,
  sortBy: '',
  sortOrder: 'desc',
  limit: 50,
  cursor: null
};

const rowsPerPageOptions = [25, 50, 100];

const parseMaybeNumber = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const cleaned = String(value).replace(/[^0-9+-.]/g, '');
  if (!cleaned || cleaned === '+' || cleaned === '-' || cleaned === '.' || cleaned === '+.' || cleaned === '-.') {
    return null;
  }
  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : null;
};

const pickDateString = (candidates, fallback) => {
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const timestamp = new Date(candidate).getTime();
    if (Number.isFinite(timestamp)) {
      return candidate;
    }
  }

  if (fallback) {
    const timestamp = new Date(fallback).getTime();
    if (Number.isFinite(timestamp)) {
      return new Date(timestamp).toISOString();
    }
  }

  return new Date().toISOString();
};

const buildTradeFromSignal = (signal) => {
  if (!signal) {
    return null;
  }

  const entryDate = pickDateString(
    [signal.entryDate, signal.date, signal.createdAt],
    new Date().toISOString()
  );

  const entryPrice = parseMaybeNumber(signal.entryPrice ?? signal.initialPrice);
  const stopLoss = parseMaybeNumber(signal.stopLoss ?? signal.stopLossLevel);
  const quantity = parseMaybeNumber(signal.quantity ?? signal.size ?? signal.positionSize);
  const rawReturnPercent = parseMaybeNumber(signal.returnPercent ?? signal.percentageGain);
  const fallbackReturnPercent = rawReturnPercent !== null ? rawReturnPercent / 100 : null;

  const exitDateCandidate = pickDateString(
    [
      signal.exitDate,
      signal.closedAt,
      signal.closeDate,
      signal.target10RHitAt,
      signal.target5RHitAt,
      signal.target3RHitAt,
      signal.stoppedOutAt
    ],
    null
  );

  const exitPriceCandidate = parseMaybeNumber(
    signal.exitPrice ?? signal.finalPrice ?? signal.closePrice ?? signal.resolutionPrice
  );

  const currentPriceCandidate = parseMaybeNumber(signal.currentPrice ?? signal.latestPrice);
  const currentDateCandidate = pickDateString(
    [signal.updatedAt, signal.lastPriceAt, signal.date],
    entryDate
  );

  const hasExitEvent = Boolean(exitDateCandidate);
  const isActive =
    signal.active === 'true' ||
    (signal.status && signal.status.toLowerCase().includes('active')) ||
    (!hasExitEvent && !signal.stoppedOutAt);

  const safeEntry = Number.isFinite(entryPrice) ? entryPrice : 0;
  const currentPrice = Number.isFinite(currentPriceCandidate) ? currentPriceCandidate : null;

  const exitDate = hasExitEvent && !isActive ? exitDateCandidate : null;
  let exitPrice =
    hasExitEvent && !isActive && Number.isFinite(exitPriceCandidate)
      ? exitPriceCandidate
      : null;

  if (exitDate && !Number.isFinite(exitPrice) && Number.isFinite(currentPrice)) {
    exitPrice = currentPrice;
  }

  const effectiveExitPrice =
    exitDate && Number.isFinite(exitPrice)
      ? exitPrice
      : Number.isFinite(currentPrice)
      ? currentPrice
      : safeEntry;

  let pnlValue = null;
  if (Number.isFinite(safeEntry) && Number.isFinite(effectiveExitPrice)) {
    const perUnit = effectiveExitPrice - safeEntry;
    // If we know the quantity/position size, scale P&L accordingly
    pnlValue = Number.isFinite(quantity) && quantity > 0 ? perUnit * quantity : perUnit;
  } else if (Number.isFinite(safeEntry) && fallbackReturnPercent !== null) {
    // Fallback: infer P&L from percentage move, scaled by notional (entry * quantity when available)
    const notional = Number.isFinite(quantity) && quantity > 0 ? safeEntry * quantity : safeEntry;
    pnlValue = notional * fallbackReturnPercent;
  }
  if (!Number.isFinite(pnlValue)) {
    pnlValue = 0;
  }

  const computedReturnPercent =
    Number.isFinite(safeEntry) && safeEntry !== 0 && Number.isFinite(effectiveExitPrice)
      ? (effectiveExitPrice - safeEntry) / safeEntry
      : null;
  const returnPercent =
    computedReturnPercent !== null
      ? computedReturnPercent
      : (fallbackReturnPercent !== null ? fallbackReturnPercent : 0);

  const durationReferenceDate = exitDate || currentDateCandidate;
  const duration =
    durationReferenceDate
      ? Math.max(
          0,
          Math.round(
            (new Date(durationReferenceDate).getTime() - new Date(entryDate).getTime()) /
              (1000 * 60 * 60 * 24)
          )
        )
      : 0;

  return {
    id: signal.signalId ?? signal.id ?? `${signal.symbol}-${entryDate}`,
    symbol: signal.symbol,
    entryDate,
    exitDate,
    entryPrice: safeEntry,
    exitPrice: Number.isFinite(exitPrice) ? exitPrice : null,
    stopLoss: Number.isFinite(stopLoss) ? stopLoss : undefined,
    // The stop in force when it trails (DWLF-341); stopLoss above stays the initial stop.
    trailingStopLevel: trailingStopLevel(signal) ?? undefined,
    pnl: Number.isFinite(pnlValue) ? pnlValue : 0,
    returnPercent,
    duration,
    quantity: Number.isFinite(quantity) ? quantity : 0,
    status:
      signal.status ??
      (signal.stoppedOutAt
        ? 'Stopped Out'
        : signal.active === 'true'
        ? 'Active'
        : 'Closed'),
    isOpen: isActive,
    currentPrice,
    currentDate: currentDateCandidate,
    strategy: signal.strategyDescription || signal.strategy,
    reason: signal.reasonSummary,
    sourceSignal: signal
  };
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 'N/A';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'N/A';
  }
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const renderRR = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 'N/A';
  }
  const color = numeric >= 0 ? 'success.main' : 'error.main';
  const prefix = numeric >= 0 ? '+' : '';
  return (
    <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
      {`${prefix}${numeric.toFixed(2)}R`}
    </Typography>
  );
};

const renderPercentage = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 'N/A';
  }
  const color = numeric >= 0 ? 'success.main' : 'error.main';
  const prefix = numeric >= 0 ? '+' : '';
  return (
    <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
      {`${prefix}${numeric.toFixed(2)}%`}
    </Typography>
  );
};

const getStatusChip = (signal) => {
  if (!signal) {
    return null;
  }

  // A trailing stop-out (DWLF-341) keeps status 'Stopped Out' but is often a profit.
  const trailed = trailingExit(signal);
  if (trailed) {
    return <Chip label="Trailing stop" color={trailed.chipColor} size="small" />;
  }

  if (signal.status) {
    const label = signal.status;
    if (label.toLowerCase().startsWith('target met') || label.toLowerCase().includes('hit')) {
      return <Chip label={label} color="success" size="small" />;
    }
    if (label === 'Stopped Out') {
      return <Chip label={label} color="error" size="small" />;
    }
    return <Chip label={label} color="default" size="small" />;
  }

  if (signal.active === 'true') {
    if (signal.target10RHitAt) {
      return <Chip label="10R Hit" color="success" size="small" />;
    }
    if (signal.target5RHitAt) {
      return <Chip label="5R Hit" color="success" size="small" />;
    }
    if (signal.target3RHitAt) {
      return <Chip label="3R Hit" color="success" size="small" />;
    }
    return <Chip label="Active" color="primary" size="small" />;
  }

  if (signal.target10RHitAt) {
    return <Chip label="10R Hit" color="success" size="small" />;
  }
  if (signal.target5RHitAt) {
    return <Chip label="5R Hit" color="success" size="small" />;
  }
  if (signal.target3RHitAt) {
    return <Chip label="3R Hit" color="success" size="small" />;
  }
  if (signal.stoppedOutAt || signal.currentRR === -1) {
    return <Chip label="Stopped Out" color="error" size="small" />;
  }
  return <Chip label="Closed" color="default" size="small" />;
};

const UserTradeSignalsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const signals = useSelector(selectUserTradeSignals);
  const summary = useSelector(selectUserTradeSignalSummary);
  const pagination = useSelector(selectUserTradeSignalPagination);
  const filters = useSelector(selectUserTradeSignalFilters);
  const stats = useSelector(selectUserTradeSignalStats);
  const tableStats = useSelector(selectUserTradeSignalTableStats);
  const loading = useSelector(selectUserTradeSignalsLoading);
  const error = useSelector(selectUserTradeSignalsError);

  const [localFilters, setLocalFilters] = useState(filters);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [pageCursors, setPageCursors] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);

  // Check if any filters are active
  const hasActiveFilters = filters.symbol || filters.strategy || filters.active || 
    filters.fromDate || filters.toDate || filters.hit3R || filters.stoppedOut;

  useEffect(() => {
    dispatch(fetchUserTradeSignalStats());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUserTradeSignals(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleTradeClick = useCallback((signal) => {
    const trade = buildTradeFromSignal(signal);
    if (!trade) {
      return;
    }
    setSelectedTrade(trade);
    setTradeModalOpen(true);
  }, []);

  const handleTradeModalClose = useCallback(() => {
    setTradeModalOpen(false);
    setSelectedTrade(null);
  }, []);

  const handleSymbolClick = useCallback(
    (event, symbol) => {
      event.stopPropagation();
      if (symbol) {
        navigate(`/markets/${symbol}`);
      }
    },
    [navigate]
  );

  const isLoadingSignals = loading.signals;
  const isInitialLoad = isLoadingSignals && signals.length === 0;

  const strategyOptions = useMemo(() => {
    const optionsMap = new Map();
    Object.entries(stats?.strategies || {}).forEach(([id, metadata]) => {
      if (!id) {
        return;
      }
      const label =
        metadata?.label ||
        metadata?.name ||
        metadata?.description ||
        id;
      optionsMap.set(id, { id, label });
    });

    signals.forEach((signal) => {
      const strategyId = signal?.strategy;
      if (!strategyId || optionsMap.has(strategyId)) {
        return;
      }
      const label =
        signal.strategyDescription ||
        signal.strategyDetails?.summary ||
        signal.strategyName ||
        signal.reasonSummary ||
        strategyId;
      optionsMap.set(strategyId, { id: strategyId, label });
    });

    return Array.from(optionsMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [stats?.strategies, signals]);

  const strategyLabelMap = useMemo(() => {
    const map = new Map();
    strategyOptions.forEach((option) => {
      map.set(option.id, option.label);
    });
    return map;
  }, [strategyOptions]);

  const summaryCards = useMemo(() => {
    const winRatePercent = summary.winRate * 100;
    return [
      {
        title: 'Total Signals',
        value: summary.total ?? 0,
        color: 'primary.main'
      },
      {
        title: 'Active Signals',
        value: tableStats.activeSignals ?? 0,
        color: 'info.main'
      },
      {
        title: 'Concluded',
        value: summary.concluded ?? 0,
        color: 'warning.main'
      },
      {
        title: 'Wins',
        value: summary.wins ?? 0,
        color: 'success.main'
      },
      {
        title: 'Losses',
        value: summary.losses ?? 0,
        color: 'error.main'
      },
      {
        title: 'Win Rate',
        value: Number.isFinite(winRatePercent)
          ? `${winRatePercent.toFixed(1)}%`
          : 'N/A',
        color: winRatePercent >= 50 ? 'success.main' : 'warning.main'
      },
      {
        title: 'Avg Current RR',
        value:
          summary.avgCurrentRR !== null && summary.avgCurrentRR !== undefined
            ? `${summary.avgCurrentRR.toFixed(2)}R`
            : 'N/A',
        color: 'secondary.main'
      },
      {
        title: 'Avg % Gain',
        value:
          summary.avgPercentageGain !== null &&
          summary.avgPercentageGain !== undefined
            ? `${summary.avgPercentageGain.toFixed(2)}%`
            : 'N/A',
        color: 'secondary.light'
      }
    ];
  }, [summary, tableStats]);

  const handleInputChange = (field) => (event) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleApplyFilters = () => {
    const sanitizedSymbol = localFilters.symbol.trim();
    const appliedFilters = {
      ...localFilters,
      symbol: sanitizedSymbol,
      cursor: null
    };
    dispatch(setFilters(appliedFilters));
    // Reset pagination state
    setPageCursors([null]);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    const cleared = {
      ...defaultFilters,
      limit: filters.limit,
      cursor: null
    };
    setLocalFilters(cleared);
    dispatch(setFilters(cleared));
    // Reset pagination state
    setPageCursors([null]);
    setCurrentPage(0);
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage > currentPage) {
      // Going forward - use cursor from current pagination state
      const nextCursor = pagination.cursor;
      if (!nextCursor && newPage > 0) return; // No more pages
      dispatch(setFilters({ ...filters, cursor: nextCursor }));
      // Save current cursor for potential back navigation
      setPageCursors((prev) => {
        const updated = prev.slice(0, newPage);
        updated[newPage] = nextCursor;
        return updated;
      });
      setCurrentPage(newPage);
    } else if (newPage < currentPage) {
      // Going back - use cached cursor from pageCursors
      const previousCursor = pageCursors[newPage] || null;
      dispatch(setFilters({ ...filters, cursor: previousCursor }));
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    if (Number.isNaN(newLimit)) {
      return;
    }
    dispatch(
      setFilters({
        ...filters,
        limit: newLimit,
        cursor: null
      })
    );
    // Reset pagination state
    setPageCursors([null]);
    setCurrentPage(0);
  };

  // For TablePagination - use tracked currentPage instead of calculating from offset

  if (isInitialLoad) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="350px" height={20} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} />
          ))}
        </Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="100px" height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={40} />
            ))}
          </Box>
        </Paper>
        <TableSkeleton rows={5} columns={11} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: { xs: '1.75rem', md: '2rem' },
            fontWeight: 600,
            letterSpacing: '-0.03em',
            color: 'var(--dwlf-text)',
            mb: 0.5,
          }}
        >
          My Signals
        </Typography>
        <Typography 
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.85rem',
            color: 'var(--dwlf-ink-50)',
          }}
        >
          Signals generated from your configured visual strategies
        </Typography>
      </Box>

      {error.signals && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.signals}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 2,
          mb: 3
        }}
      >
        {summaryCards.map((card) => (
          <motion.div
            key={card.title}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  backgroundColor: card.color
                }}
              />
              <CardContent>
                <Typography
                  color="textSecondary"
                  gutterBottom
                  variant="body2"
                  fontWeight={600}
                >
                  {card.title}
                </Typography>
                <Typography variant="h4" fontWeight={700} color="text.primary">
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr)' },
            gap: 2,
            alignItems: 'center'
          }}
        >
          <Box>
            <TextField
              label="Symbol"
              value={localFilters.symbol}
              onChange={handleInputChange('symbol')}
              fullWidth
              size="small"
              placeholder="e.g. AAPL"
            />
          </Box>
          <Box>
            <TextField
              label="From Date"
              type="date"
              value={localFilters.fromDate}
              onChange={handleInputChange('fromDate')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <TextField
              label="To Date"
              type="date"
              value={localFilters.toDate}
              onChange={handleInputChange('toDate')}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.active}
                label="Status"
                onChange={handleInputChange('active')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localFilters.hit3R}
                  onChange={handleSwitchChange('hit3R')}
                  size="small"
                />
              }
              label="3R Target Hit"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localFilters.stoppedOut}
                  onChange={handleSwitchChange('stoppedOut')}
                  size="small"
                />
              }
              label="Stopped Out"
            />
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Strategy</InputLabel>
              <Select
                value={localFilters.strategy}
                label="Strategy"
                onChange={handleInputChange('strategy')}
              >
                <MenuItem value="">All</MenuItem>
                {strategyOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={localFilters.sortBy}
                label="Sort By"
                onChange={handleInputChange('sortBy')}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="currentRR">Current RR</MenuItem>
                <MenuItem value="percentageGain">% Gain</MenuItem>
                <MenuItem value="confidenceScore">Confidence</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={localFilters.sortOrder}
                label="Order"
                onChange={handleInputChange('sortOrder')}
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ gridColumn: { xs: '1 / -1' } }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading.signals}
              >
                Apply Filters
              </Button>
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {signals.length === 0 && !loading.signals ? (
        hasActiveFilters ? (
          <EmptyState
            icon={<SignalIcon sx={{ fontSize: 80, color: 'var(--dwlf-accent)' }} />}
            title="No signals found"
            subtitle="No signals match the selected filters. Try adjusting your filter criteria."
            buttonLabel="Clear Filters"
            onAction={handleClearFilters}
          />
        ) : (
          <EmptyState
            icon={<SignalIcon sx={{ fontSize: 80, color: 'var(--dwlf-accent)' }} />}
            title="No signals yet"
            subtitle="Signals will appear here once you configure strategies with symbols. Strategies generate signals when market conditions match your criteria."
            buttonLabel="Configure Strategies"
            onAction={() => navigate('/strategies')}
          />
        )
      ) : (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Results</Typography>
          {loading.signals && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DWLFLoader size="small" showText={false} />
              <Typography variant="body2" color="textSecondary">
                Updating…
              </Typography>
            </Box>
          )}
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Strategy</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>Stop Loss</TableCell>
                <TableCell>Current Price</TableCell>
                <TableCell>Current R:R</TableCell>
                <TableCell>% Gain</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Confidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.map((signal, index) => {
                const strategyLabel =
                  signal.strategyDescription ||
                  strategyLabelMap.get(signal.strategy) ||
                  signal.strategyDetails?.summary ||
                  signal.strategyName ||
                  signal.reasonSummary ||
                  signal.strategy ||
                  'N/A';

                const rowKey = signal.signalId || `${signal.symbol}-${signal.contextHash || 'ctx'}-${index}`;

                return (
                  <TableRow
                    hover
                    key={rowKey}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleTradeClick(signal)}
                  >
                    <TableCell>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ fontWeight: 700, color: 'inherit', textTransform: 'none', minWidth: 0, px: 0 }}
                        onClick={(event) => handleSymbolClick(event, signal.symbol)}
                      >
                        {signal.symbol}
                      </Button>
                    </TableCell>
                    <TableCell>{formatDate(signal.date || signal.createdAt)}</TableCell>
                    <TableCell>{strategyLabel}</TableCell>
                    <TableCell>{formatCurrency(signal.initialPrice)}</TableCell>
                    {/* A trailing stop shows its level now (DWLF-341); R stays measured from stopLossLevel. */}
                    <TableCell>
                      {formatCurrency(stopInForce(signal))}
                      {trailingStopLevel(signal) !== null && <Typography component="span" variant="caption" color="text.secondary"> trailing</Typography>}
                    </TableCell>
                    <TableCell>{formatCurrency(signal.currentPrice)}</TableCell>
                    <TableCell>{renderRR(signal.currentRR)}</TableCell>
                    <TableCell>{renderPercentage(signal.percentageGain)}</TableCell>
                    <TableCell>{getStatusChip(signal)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap>
                        {signal.reasonSummary || signal.strategyDetails?.summary || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {signal.confidenceScore !== undefined && signal.confidenceScore !== null
                        ? `${(Number(signal.confidenceScore) * 100).toFixed(0)}%`
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={currentPage}
          onPageChange={handlePageChange}
          rowsPerPage={filters.limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </Paper>
      )}
      <TradeChartModal
        open={tradeModalOpen}
        onClose={handleTradeModalClose}
        trade={selectedTrade}
      />
    </Container>
  );
};

export default UserTradeSignalsPage;
